'use client'

import { ButtonLink, CmsLink, Heading } from '@/components'
import { defaultLocale } from '@/constants'
import type { GeneralSetting } from '@/payload-types'
import { useParams } from 'next/navigation'
import type { TypedLocale } from 'payload'
import { Suspense, useMemo } from 'react'
import { createTranslationResource } from '@/lib/translations'

interface TranslationsFallbackProps {
  otherPages: Array<{
    link?: string | null
    title: string
    locale: TypedLocale
  }>
  additionalLinks: GeneralSetting['fallbackRecommendedLinks'] | null
}

const FALLBACK_TRANSLATION_KEYS = [
  'fallback.title',
  'fallback.message',
  'fallback.additionalLinks',
] as const

const BUTTON_SKELETON_KEYS = [
  'fallback-button-primary',
  'fallback-button-secondary',
  'fallback-button-tertiary',
  'fallback-button-quaternary',
]

const ADDITIONAL_LINK_SKELETON_KEYS = [
  'fallback-additional-primary',
  'fallback-additional-secondary',
  'fallback-additional-tertiary',
]

const resolveFallbackTranslations = (map: Record<string, string>) => ({
  title: map['fallback.title'] ?? 'fallback.title',
  message: map['fallback.message'] ?? 'fallback.message',
  additionalLinks: map['fallback.additionalLinks'] ?? 'fallback.additionalLinks',
})

export function TranslationsFallback({
  otherPages,
  additionalLinks,
}: TranslationsFallbackProps) {
  const params = useParams()
  const locale = (params?.locale as TypedLocale) || defaultLocale
  const translationResource = useMemo(
    () => createTranslationResource([...FALLBACK_TRANSLATION_KEYS], locale),
    [locale],
  )

  const hasAdditionalLinks = Boolean(
    additionalLinks?.links && additionalLinks.links.length > 0,
  )
  const visibleOtherPages = otherPages.filter((page) => Boolean(page.link))
  const skeletonButtonKeys = BUTTON_SKELETON_KEYS.slice(
    0,
    Math.min(BUTTON_SKELETON_KEYS.length, visibleOtherPages.length || 2),
  )

  return (
    <div className="container py-28">
      <Suspense
        fallback={
          <TranslationsFallbackSkeleton
            buttonKeys={skeletonButtonKeys}
            showAdditionalLinks={hasAdditionalLinks}
          />
        }
      >
        <TranslationsFallbackContent
          translationResource={translationResource}
          otherPages={visibleOtherPages}
          additionalLinks={additionalLinks}
          locale={locale}
        />
      </Suspense>
    </div>
  )
}

const TranslationsFallbackContent = ({
  translationResource,
  otherPages,
  additionalLinks,
  locale,
}: {
  translationResource: ReturnType<typeof createTranslationResource>
  otherPages: Array<{
    link?: string | null
    title: string
    locale: TypedLocale
  }>
  additionalLinks: GeneralSetting['fallbackRecommendedLinks'] | null
  locale: TypedLocale
}) => {
  const translations = resolveFallbackTranslations(translationResource.read())

  return (
    <>
      <div className="text-center mb-8">
        <Heading as="h1" text={translations.title} size="3xl" />
        <p className="mt-6">{translations.message}</p>
      </div>
      <div className="flex items-center flex-col gap-4 justify-center">
        {otherPages.map(({ link, title, locale: pageLocale }) =>
          link ? (
            <ButtonLink
              text={`${title} (${pageLocale})`}
              href={link}
              color="dark-grey"
              key={link}
            />
          ) : null,
        )}
      </div>
      {additionalLinks?.links && additionalLinks.links.length > 0 && (
        <div className="mt-6 text-center">
          <p>{translations.additionalLinks}</p>
          <ul className="mt-4 flex flex-col gap-2 items-center">
            {additionalLinks.links.map(({ label, link, id }) =>
              link ? (
                <li key={id}>
                  <CmsLink
                    locale={locale}
                    className="gpf-button"
                    reference={link.reference}
                    newTab={link.newTab}
                    label={label}
                    type={link.type}
                    url={link.url}
                    productsCategorySlug={link.productsCategorySlug}
                  />
                </li>
              ) : null,
            )}
          </ul>
        </div>
      )}
    </>
  )
}

const TranslationsFallbackSkeleton = ({
  buttonKeys,
  showAdditionalLinks,
}: {
  buttonKeys: string[]
  showAdditionalLinks: boolean
}) => (
  <div aria-hidden="true">
    <div className="text-center mb-8">
      <div
        className="gpf-skeleton gpf-skeleton--inline"
        style={{ width: '16rem', minHeight: '3rem', margin: '0 auto 1rem' }}
      >
        &nbsp;
      </div>
      <div
        className="gpf-skeleton gpf-skeleton--inline"
        style={{ width: '24rem', minHeight: '1.5rem', margin: '0 auto' }}
      >
        &nbsp;
      </div>
    </div>
    <div className="flex items-center flex-col gap-4 justify-center">
      {buttonKeys.map((key) => (
        <div
          key={key}
          className="gpf-skeleton gpf-skeleton--inline"
          style={{ width: '12rem', minHeight: '2.75rem' }}
        >
          &nbsp;
        </div>
      ))}
    </div>
    {showAdditionalLinks && (
      <div className="mt-8 text-center">
        <div
          className="gpf-skeleton gpf-skeleton--inline"
          style={{ width: '14rem', minHeight: '1.25rem', margin: '0 auto 1rem' }}
        >
          &nbsp;
        </div>
        <div className="flex flex-col gap-3 items-center">
          {ADDITIONAL_LINK_SKELETON_KEYS.map((key) => (
            <div
              key={key}
              className="gpf-skeleton gpf-skeleton--inline"
              style={{ width: '10rem', minHeight: '2.25rem' }}
            >
              &nbsp;
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
)
